import { Aide } from '@/components/ui/aide';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Banknote,
  CheckCircle2,
  Fingerprint,
  Loader2,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react';
import {
  arbitrationAPI,
  dataFintechAPI,
  documentsAPI,
  missionIntegrityAPI,
  operationalLedgerAPI,
  proofOfDeliveryAPI,
} from '../lib/api';
import { ledgerEntriesAPI, tripsAPI } from '../lib/api';
import { fetchAllPages } from '../lib/fetchAllPages';
import { BarrePagination } from '../components/ui/pagination-bar';
import { usePaginationServeur } from '../hooks/usePagination';
import { useDerniereDemande } from '../hooks/useDerniereDemande';
import { tripOption } from '../lib/selectOptions';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { cn, formatCurrency, formatVolume } from '../lib/utils';
import { PageHeader } from '../components/ui/page-header';
import { ColonneFixe } from '../components/ui/page-layouts';
import { LoadingState } from '../components/ui/loading-state';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { EmptyState } from '../components/ui/empty-state';
import { useAuth } from '../context/AuthContext';
import { messageErreur } from '../lib/erreurs';
import { formatDate, formatDateHeure } from '../lib/formats';
import { libelle, texteSur } from '../lib/libelles';

const severityTone = {
  critical: 'border-destructive/30 bg-destructive/10 text-destructive',
  error: 'border-destructive/30 bg-destructive/10 text-destructive',
  warning: 'border-flame/30 bg-flame/10 text-flame-ink',
  info: 'border-info/30 bg-info/10 text-info',
};

export default function MissionIntegrityPage() {
  const { hasModule } = useAuth();
  const canUseAdvancedAudit = hasModule('advanced_audit');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [anomalies, setAnomalies] = useState([]);
  // Le relevé se lit par pages: son total et les chiffres de l'en-tête viennent
  // du serveur, qui compte toutes les anomalies ouvertes, pas la seule page.
  const [anomaliesTotal, setAnomaliesTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const pagination = usePaginationServeur(anomaliesTotal);
  const { skip, limit } = pagination;
  const [ledger, setLedger] = useState(null);
  // L'écran n'attend le squelette qu'au premier chargement du relevé et du contexte.
  const [anomaliesReady, setAnomaliesReady] = useState(false);
  const [contextReady, setContextReady] = useState(false);
  const loading = !(anomaliesReady && contextReady);
  const [tripId, setTripId] = useState('');
  const [documentTripId, setDocumentTripId] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [justificationScoreId, setJustificationScoreId] = useState('');
  const [justification, setJustification] = useState('');
  const [working, setWorking] = useState(false);
  const [scoreSnapshot, setScoreSnapshot] = useState(null);
  const [arbitrationTripId, setArbitrationTripId] = useState('');
  const [arbitrationSummary, setArbitrationSummary] = useState(null);
  const [trips, setTrips] = useState([]);
  const [ledgerEntries, setLedgerEntries] = useState(null);
  const [showLedgerEntries, setShowLedgerEntries] = useState(false);
  const [tripDocuments, setTripDocuments] = useState([]);

  useEffect(() => {
    const tripFromQuery = searchParams.get('trip_id') || '';
    const scoreFromQuery = searchParams.get('score_id') || '';
    const documentTripFromQuery = searchParams.get('document_trip_id') || tripFromQuery;
    if (tripFromQuery) {
      setTripId(tripFromQuery);
      setArbitrationTripId(tripFromQuery);
    }
    if (documentTripFromQuery) {
      setDocumentTripId(documentTripFromQuery);
    }
    if (scoreFromQuery) {
      setJustificationScoreId(scoreFromQuery);
    }
  }, [searchParams]);

  // Le relevé d'une page (`skip`/`limit`): se recharge à chaque page ou taille
  // choisie, sans refaire le journal, le score ni la liste des trajets.
  const nouvelleDemande = useDerniereDemande();
  const loadAnomalies = useCallback(async () => {
    const perimee = nouvelleDemande();
    try {
      const result = await missionIntegrityAPI.getAnomaliesPage({ status: 'open', skip, limit });
      if (perimee()) return;
      setAnomalies(Array.isArray(result.items) ? result.items : []);
      setAnomaliesTotal(result.total);
      setSummary(result.summary);
    } catch (err) {
      if (perimee()) return;
      toast.error(messageErreur(err, 'Impossible de charger les livraisons à contrôler'));
    } finally {
      if (!perimee()) setAnomaliesReady(true);
    }
  }, [skip, limit, nouvelleDemande]);

  const loadContext = useCallback(async () => {
    try {
      const [chain, score, tripsPage] = await Promise.all([
        canUseAdvancedAudit
          ? operationalLedgerAPI.verify().catch(() => null)
          : Promise.resolve(null),
        canUseAdvancedAudit
          ? dataFintechAPI.getScore().catch(() => null)
          : Promise.resolve(null),
        fetchAllPages((params) => tripsAPI.getAllPaged(params)).catch(() => ({ items: [] })),
      ]);
      setLedger(chain);
      setScoreSnapshot(score);
      setTrips(tripsPage.items || []);
    } catch (err) {
      toast.error(messageErreur(err, 'Impossible de charger les livraisons à contrôler'));
    } finally {
      setContextReady(true);
    }
  }, [canUseAdvancedAudit]);

  const load = useCallback(
    () => Promise.all([loadAnomalies(), loadContext()]),
    [loadAnomalies, loadContext],
  );

  useEffect(() => {
    loadAnomalies();
  }, [loadAnomalies]);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  const metrics = useMemo(() => {
    // Les chiffres du serveur portent sur tout le relevé; sans eux, sur la page reçue.
    if (summary) {
      return {
        critical: summary.critical,
        liters: summary.liters,
        money: summary.amount,
        missingPod: summary.missing_pod,
      };
    }
    const critical = anomalies.filter((item) => item.severity === 'critical').length;
    const liters = anomalies.reduce((sum, item) => sum + Math.abs(Number(item.value_liters || 0)), 0);
    const money = anomalies.reduce((sum, item) => sum + Number(item.value_amount || 0), 0);
    const missingPod = anomalies.filter((item) => item.anomaly_type === 'missing_pod').length;
    return { critical, liters, money, missingPod };
  }, [anomalies, summary]);

  const tripOptions = useMemo(
    () => trips.map((trip) => ({ value: trip.id, ...tripOption(trip) })),
    [trips],
  );

  // Les pièces d'une livraison se choisissent dans une liste: l'opérateur
  // reconnaît un bon de livraison à son type et à sa date.
  useEffect(() => {
    if (!documentTripId) {
      setTripDocuments([]);
      return undefined;
    }
    let annule = false;
    documentsAPI
      .getAll()
      .then((rows) => {
        if (annule) return;
        setTripDocuments(
          rows.filter((doc) => (doc.associated_trip_id || doc.trip_id) === documentTripId),
        );
      })
      .catch(() => {
        if (!annule) setTripDocuments([]);
      });
    return () => {
      annule = true;
    };
  }, [documentTripId]);

  const documentOptions = useMemo(
    () =>
      tripDocuments.map((doc) => ({
        value: doc.id,
        label: [libelle('documentType', doc.doc_type), formatDate(doc.created_at, '')]
          .filter(Boolean)
          .join(' · '),
      })),
    [tripDocuments],
  );

  const selectedAnomaly = useMemo(
    () => anomalies.find((item) => item.score_id && item.score_id === justificationScoreId) || null,
    [anomalies, justificationScoreId],
  );

  const tripLabelById = useMemo(
    () => new Map(trips.map((trip) => [trip.id, tripOptionLabel(trip)])),
    [trips],
  );

  const recompute = async () => {
    if (!tripId.trim()) {
      toast.error('Choisissez la livraison à recalculer');
      return;
    }
    setWorking(true);
    try {
      await missionIntegrityAPI.recomputeTrip(tripId.trim());
      toast.success('Contrôle de la livraison recalculé');
      setTripId('');
      await load();
    } finally {
      setWorking(false);
    }
  };

  const validatePod = async () => {
    if (!documentTripId.trim() || !documentId.trim()) {
      toast.error('Choisissez la livraison et le document');
      return;
    }
    setWorking(true);
    try {
      await proofOfDeliveryAPI.validateDocument(documentTripId.trim(), documentId.trim(), false);
      toast.success('Preuve de livraison mise à jour');
      setDocumentTripId('');
      setDocumentId('');
      await load();
    } finally {
      setWorking(false);
    }
  };

  const justify = async () => {
    if (!justificationScoreId.trim() || !justification.trim()) {
      toast.error('Choisissez l’écart à justifier et expliquez-le');
      return;
    }
    setWorking(true);
    try {
      await missionIntegrityAPI.justify(justificationScoreId.trim(), justification.trim());
      toast.success('Justification enregistrée');
      setJustificationScoreId('');
      setJustification('');
      await load();
    } finally {
      setWorking(false);
    }
  };

  const recomputeScore = async () => {
    setWorking(true);
    try {
      const score = await dataFintechAPI.recomputeScore();
      setScoreSnapshot(score);
      toast.success('Score E-Carbu recalculé');
    } finally {
      setWorking(false);
    }
  };

  const downloadBankAudit = async () => {
    setWorking(true);
    try {
      const blob = await dataFintechAPI.downloadBankAuditReport();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'rapport-audit-banque.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setWorking(false);
    }
  };

  const selectArbitrationTrip = async (tripId) => {
    setArbitrationTripId(tripId);
    // Un certificat déjà émis se relit sans être régénéré: régénérer réécrit
    // le PDF et ajoute un maillon au ledger pour une simple consultation.
    const certificate = await arbitrationAPI.get(tripId).catch(() => null);
    setArbitrationSummary(certificate?.summary_json || null);
  };

  const generateArbitration = async () => {
    if (!arbitrationTripId) {
      toast.error('Choisissez un trajet');
      return;
    }
    setWorking(true);
    try {
      const certificate = await arbitrationAPI.create(arbitrationTripId);
      setArbitrationSummary(certificate?.summary_json || null);
      window.open(arbitrationAPI.getPdfUrl(arbitrationTripId), '_blank', 'noopener,noreferrer');
      toast.success('Certificat d’arbitrage généré');
    } finally {
      setWorking(false);
    }
  };

  const openLedgerEntries = async () => {
    setShowLedgerEntries(true);
    if (ledgerEntries) return;
    try {
      setLedgerEntries(await ledgerEntriesAPI.getRecent(50));
    } catch (err) {
      setLedgerEntries([]);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
        <LoadingState shape="readouts" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Écarts à surveiller"
        title="Livraisons à contrôler"
        description="Pertes de carburant, bons de livraison manquants et montants à surveiller. Ces alertes sont informatives : la facturation et le paiement restent possibles."
        // Quatre chiffres en 2×2, sans pastille « Plus » pour un seul chiffre
        // caché ni case orpheline : les écarts critiques ont déjà leur pastille
        // d'alerte, ils sont rappelés dans l'aide des exceptions ouvertes.
        stats={[
          {
            label: 'Exceptions ouvertes',
            valeur: anomaliesTotal,
            ton: anomaliesTotal ? 'critique' : 'positif',
            aide: `${metrics.critical} critique${metrics.critical > 1 ? 's' : ''}`,
          },
          {
            label: 'Montant en jeu',
            valeur: formatCurrency(metrics.money),
            ton: metrics.money ? 'critique' : 'positif',
          },
          {
            label: 'Litres douteux',
            valeur: formatVolume(metrics.liters),
            ton: metrics.liters ? 'attention' : 'positif',
          },
          {
            label: 'BL manquants',
            valeur: metrics.missingPod,
            ton: metrics.missingPod ? 'attention' : 'positif',
            aide: 'Bons de livraison manquants',
          },
        ]}
        alertesSousTitre
        alertes={[
          metrics.critical > 0 && {
            niveau: 'critique',
            nombre: metrics.critical,
            court: metrics.critical > 1 ? 'écarts critiques' : 'écart critique',
            texte: `${metrics.critical} écart${metrics.critical > 1 ? 's' : ''} critique${metrics.critical > 1 ? 's' : ''} à traiter ou à justifier en priorité.`,
          },
        ]}
        actions={
          <Button variant="outline" onClick={load} disabled={working}>
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Actualiser
          </Button>
        }
      />

      <section className="grid gap-4 xl:grid-cols-[1fr_420px]">
        <div className="space-y-4">
          <Card>
            <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="flex items-center gap-1">
                  <CardTitle>Exceptions à traiter</CardTitle>
                  <Aide titre="Exceptions à traiter">Les écarts constatés sur les livraisons, à traiter ou à justifier.</Aide>
                </div>
              </div>
              <Badge className="w-fit border-border bg-muted/50 text-muted-foreground">
                {anomaliesTotal} ouverte{anomaliesTotal > 1 ? 's' : ''}
              </Badge>
            </CardHeader>
            <CardContent className="space-y-3">
              {anomalies.map((item) => (
                <AnomalyCard
                  key={item.id}
                  item={item}
                  tripLabel={tripLabelById.get(item.trip_id)}
                  onUseScore={() => setJustificationScoreId(item.score_id || '')}
                  onUseTrip={() => setTripId(item.trip_id || '')}
                />
              ))}
              {/* « Aucune exception » sur une flotte qui roule est une bonne
                  nouvelle et se dit en vert. Sur une société sans aucun
                  trajet, la même phrase ferait croire que tout a été vérifié
                  alors que rien n'a été mesuré: cas séparé. */}
              {anomalies.length > 0 && (
                <BarrePagination pagination={pagination} nom="exceptions" />
              )}
              {anomalies.length === 0 &&
                (trips.length === 0 ? (
                  <EmptyState
                    icon={ShieldAlert}
                    title="Rien à contrôler pour l’instant"
                    description="Cet écran compare ce qui a été chargé, livré, prouvé et encaissé sur chaque livraison, et ne montre que les écarts. Il attend une première livraison."
                    actionLabel="Voir les trajets"
                    onAction={() => navigate('/trips')}
                    className="border-0"
                  />
                ) : (
                  <div className="rounded-lg border border-success/30 bg-success/10 p-5 text-sm font-semibold text-success">
                    Aucune exception ouverte : toutes les livraisons contrôlées sont en règle.
                  </div>
                ))}
            </CardContent>
          </Card>
        </div>

        <ColonneFixe as="aside" point="xl" className="space-y-4">
          <ActionPanel
            title="Recalculer une livraison"
            description="À utiliser après une livraison, un rapprochement, un document ou une correction de position."
            actionLabel="Recalculer"
            disabled={working}
            onSubmit={recompute}
          >
            <SearchableSelect
              placeholder="Choisir une livraison"
              searchPlaceholder="Rechercher un trajet…"
              emptyMessage="Aucun trajet trouvé"
              value={tripId || ''}
              onValueChange={setTripId}
              options={tripOptions}
            />
          </ActionPanel>

          <ActionPanel
            title="Valider un bon de livraison"
            description="Met à jour la preuve de livraison puis relance le contrôle de la livraison."
            actionLabel="Analyser le document"
            disabled={working}
            onSubmit={validatePod}
          >
            <SearchableSelect
              placeholder="Choisir une livraison"
              searchPlaceholder="Rechercher un trajet…"
              emptyMessage="Aucun trajet trouvé"
              value={documentTripId || ''}
              onValueChange={(value) => {
                setDocumentTripId(value);
                setDocumentId('');
              }}
              options={tripOptions}
            />
            <SearchableSelect
              placeholder={documentTripId ? 'Choisir le document' : 'Choisissez d’abord la livraison'}
              searchPlaceholder="Rechercher un document…"
              emptyMessage="Aucun document rattaché à cette livraison"
              value={documentId || ''}
              onValueChange={setDocumentId}
              options={documentOptions}
            />
          </ActionPanel>

          <ActionPanel
            title="Justifier un écart"
            description="Garde une trace de l’explication donnée, sans suspendre les opérations."
            actionLabel="Justifier"
            disabled={working}
            onSubmit={justify}
          >
            <p className="rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
              {justificationScoreId
                ? `Écart choisi : ${texteSur(selectedAnomaly?.title, 'écart sélectionné')}`
                : 'Cliquez sur « Justifier » sous une exception pour la choisir.'}
            </p>
            <Textarea value={justification} onChange={(e) => setJustification(e.target.value)} placeholder="Pourquoi l'écart est accepté ou reste à traiter ?" />
          </ActionPanel>

          {canUseAdvancedAudit && <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Fingerprint className="h-5 w-5 text-muted-foreground" />
                Journal de contrôle
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={cn(
                'rounded-lg border p-4',
                ledger?.ok ? 'border-success/30 bg-success/10 text-success' : 'border-flame/30 bg-flame/10 text-flame-ink',
              )}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-black">{ledger?.ok ? 'Journal intact' : 'Journal non vérifié'}</p>
                    <p className="mt-1 text-sm">Écritures vérifiées : {ledger?.count ?? 0}</p>
                  </div>
                  {ledger?.ok ? <CheckCircle2 className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
                </div>
              </div>
              {/* « Journal intact » sans écriture lisible n'est pas une preuve:
                  les dernières écritures sont consultables à côté du verdict. */}
              <Button
                variant="outline"
                className="mt-3 w-full"
                onClick={openLedgerEntries}
              >
                Voir les 50 dernières écritures
              </Button>
            </CardContent>
          </Card>}

          {canUseAdvancedAudit && <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Banknote className="h-5 w-5 text-muted-foreground" />
                Fiabilité pour la banque
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="rounded-lg border border-border bg-muted/50 p-4">
                <p className="text-xs font-black uppercase tracking-wide text-muted-foreground">Score E-Carbu 90 jours</p>
                <p className="kpi-value mt-2 text-foreground">
                  {Number(scoreSnapshot?.score_value || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })}
                </p>
                <div className="mt-3 grid gap-2 text-sm text-muted-foreground">
                  <span>Bons de livraison conformes : {Math.round(Number(scoreSnapshot?.taux_bl_conformes || 0) * 100)}%</span>
                  <span>Respect des heures d’arrivée : {Math.round(Number(scoreSnapshot?.taux_ponctualite_eta || 0) * 100)}%</span>
                  <span>Concordance des volumes : {Math.round(Number(scoreSnapshot?.indice_reconciliation_volume || 0) * 100)}%</span>
                </div>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <Button variant="outline" onClick={recomputeScore} disabled={working}>Recalculer</Button>
                <Button onClick={downloadBankAudit} disabled={working}>Exporter le rapport</Button>
              </div>
            </CardContent>
          </Card>}

          <ActionPanel
            title="Certificat d’arbitrage"
            description="Produit le certificat PDF avec la preuve de livraison et la position GPS du camion."
            actionLabel="Générer le certificat"
            disabled={working}
            onSubmit={generateArbitration}
          >
            {/* Un identifiant technique saisi à la main n'est pas un choix:
                l'opérateur reconnaît un trajet à son camion et à sa date. */}
            <SearchableSelect
              placeholder="Choisir un trajet"
              searchPlaceholder="Rechercher un trajet…"
              emptyMessage="Aucun trajet trouvé"
              value={arbitrationTripId || ''}
              onValueChange={selectArbitrationTrip}
              options={tripOptions}
            />
            <ArbitrationSummary summary={arbitrationSummary} />
          </ActionPanel>
        </ColonneFixe>
      </section>

      <LedgerEntriesDialog
        open={showLedgerEntries}
        onOpenChange={setShowLedgerEntries}
        entries={ledgerEntries}
      />
    </div>
  );
}

/** Un trajet se reconnaît à son camion, son itinéraire et sa date, pas à son UUID. */
function tripOptionLabel(trip) {
  return tripOption(trip).label;
}

/* Les contrôles d'arbitrage étaient calculés, écrits dans le PDF, et jamais
   montrés à l'écran: l'opérateur devait ouvrir le PDF pour savoir pourquoi une
   livraison n'était pas certifiée. */
const ARBITRATION_CHECKS = [
  ['vehicle_presence_validated', 'Présence du camion confirmée par son GPS'],
  ['proof_photo_captured', 'Photo de preuve capturée depuis l’application'],
  ['network_time_validated', 'Heure de livraison relevée sur le réseau'],
];

const PHONE_TRUCK_DISTANCE_LIMIT_M = 300;

function ArbitrationSummary({ summary }) {
  if (!summary) return null;

  const distance = summary.gps_phone_truck_distance_m;
  const distanceOk = distance === null || distance === undefined || distance <= PHONE_TRUCK_DISTANCE_LIMIT_M;

  return (
    <div className="space-y-2 rounded-lg border border-border bg-muted/40 p-3">
      <Badge
        className={
          summary.status === 'LIVRAISON_CERTIFIEE_CONFORME'
            ? 'border-success/30 bg-success/10 text-success'
            : 'border-flame/30 bg-flame/10 text-flame-ink'
        }
      >
        {summary.status === 'LIVRAISON_CERTIFIEE_CONFORME'
          ? 'Livraison certifiée conforme'
          : 'Vérification nécessaire avant certification'}
      </Badge>
      <ul className="space-y-1.5 text-sm">
        {ARBITRATION_CHECKS.map(([key, label]) => (
          <li key={key} className="flex items-start gap-2">
            {summary[key] ? (
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden="true" />
            ) : (
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-flame-ink" aria-hidden="true" />
            )}
            <span className="text-muted-foreground">{label}</span>
          </li>
        ))}
        <li className="flex items-start gap-2">
          {distanceOk ? (
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden="true" />
          ) : (
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-flame-ink" aria-hidden="true" />
          )}
          <span className="text-muted-foreground">
            Distance téléphone ↔ camion :{' '}
            <span className="font-mono tabular-nums text-foreground">
              {distance === null || distance === undefined ? 'non mesurée' : `${distance} m`}
            </span>{' '}
            (seuil {PHONE_TRUCK_DISTANCE_LIMIT_M} m)
          </span>
        </li>
      </ul>
    </div>
  );
}

function LedgerEntriesDialog({ open, onOpenChange, entries }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <div className="flex items-center gap-1">
            <DialogTitle>Dernières écritures du journal</DialogTitle>
            <Aide titre="Journal de contrôle">
              Chaque écriture est liée à la précédente. Une ligne « Rompue » signale une
              écriture qui a été modifiée après coup.
            </Aide>
          </div>
          <DialogDescription className="sr-only">
            Chaque écriture est liée à la précédente. Une ligne « Rompue » signale une
            écriture qui a été modifiée après coup.
          </DialogDescription>
        </DialogHeader>

        {entries === null ? (
          <LoadingState shape="table" />
        ) : entries.length === 0 ? (
          <EmptyState
            variant="empty"
            icon={Fingerprint}
            title="Aucune écriture dans le journal"
            description="Le journal se remplit dès la première action tracée : contrôle d’une livraison, justification, certificat d’arbitrage."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date et heure</TableHead>
                <TableHead>Opération</TableHead>
                <TableHead>Chaînage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="whitespace-nowrap font-mono text-xs tabular-nums">
                    {formatDateHeure(entry.occurred_at)}
                  </TableCell>
                  <TableCell>
                    <p className="font-medium text-foreground">{libelle('journalEvenement', entry.event_type)}</p>
                    <p className="text-xs text-muted-foreground">
                      N° {entry.sequence_number} · {libelle('journalEntite', entry.entity_type)}
                    </p>
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={
                        entry.chain_status === 'intact'
                          ? 'border-success/30 bg-success/10 text-success'
                          : 'border-destructive/30 bg-destructive/10 text-destructive'
                      }
                    >
                      {entry.chain_status === 'intact' ? 'Intact' : 'Rompu'}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}


function AnomalyCard({ item, tripLabel, onUseScore, onUseTrip }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={severityTone[item.severity] || severityTone.info}>
              {libelle('alerteSeverity', item.severity, 'Information')}
            </Badge>
            <Badge className="border-border bg-muted/50 text-muted-foreground">
              {libelle('anomalieMission', item.anomaly_type)}
            </Badge>
          </div>
          <h2 className="mt-3">{texteSur(item.title, libelle('anomalieMission', item.anomaly_type))}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{texteSur(item.message, '')}</p>
          {tripLabel && (
            <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold text-muted-foreground">
              <span>Trajet : {tripLabel}</span>
            </div>
          )}
        </div>
        <div className="grid min-w-[180px] gap-2 text-right">
          <p className="text-sm font-bold text-muted-foreground">{formatVolume(Math.abs(Number(item.value_liters || 0)))}</p>
          <p className="text-sm font-bold text-destructive">{formatCurrency(Number(item.value_amount || 0))}</p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={onUseTrip}>Recalculer</Button>
        {item.score_id && <Button size="sm" variant="outline" onClick={onUseScore}>Justifier</Button>}
      </div>
    </div>
  );
}

function ActionPanel({ title, description, actionLabel, disabled, onSubmit, children }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-1">
          <CardTitle>{title}</CardTitle>
          {description && <Aide titre={title}>{description}</Aide>}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {children}
        <Button className="w-full" onClick={onSubmit} disabled={disabled}>
          {disabled && <Loader2 className="h-4 w-4 animate-spin" />}
          {actionLabel}
        </Button>
      </CardContent>
    </Card>
  );
}
